import React, { memo, useCallback, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, OverlayTrigger, Popover } from 'react-bootstrap';
import { isObject } from 'lodash';
import { useProductListingState } from '../../RangePlanProductListingProvider';
import noImage from '../../../../../assets/images/no-image.png';
import { shortenandHoverString } from '../../../../../Components/helper';
import CommonImageComponent from '../../../../../Components/Utility/CommonImage';
import Loader from '../../../../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../../../../Components/ModalPopup/ConfirmationPopup';
import {
  barcodeActionFor,
  productColumnMap,
  useRangePlanIdentifierColumns,
} from './columns';
import {
  EMPTY_CELL,
  EXPAND_ALL_CONFIRM_THRESHOLD,
  I18N_NAMESPACE,
} from './product-identifier-v2.constants';
import type {
  RangePlanIdentifierRow,
  RangePlanProduct,
  RangePlanTableColumn,
  ResolvedColumn,
  ViewLabels,
} from './types';
import IdentifierCell from './IdentifierCell';
import IdentifierActionHeader, { IdentifierHeaderLabel } from './IdentifierActionHeader';
import { useIdentifierBarcodeActions } from './useIdentifierBarcodeActions';
import { useProductLandingPath } from '../../../../../Components/CustomHooks/useProductLandingPath';

/**
 * BM-9586 — the Range Plan Product Identifier tab, reading `DevorderProductIdentifiers`.
 *
 * Deliberately the same hand-rolled table as v1 — same Bootstrap classes, same sticky column, same
 * expand rows — so the tab does not change shape when the flag flips. What changed is underneath:
 *
 * - **Columns come from the server.** v1 fetches `ProductIdentifierMaster` through redux and binds
 *   cells by column NAME. This reads the resolved column set off each product and binds by
 *   `identifierId`, so a renamed master no longer blanks its column — and there is no redux read
 *   at all, which removes v1's whole-store subscription.
 * - **Each product row group is memoised.** v1 re-renders every product when one is expanded or
 *   one checkbox is ticked.
 * - **Child rows key on `rowKey`**, the combination identity, not the array index.
 *
 * The v1 component is untouched and still renders whenever the flag is off.
 */

interface ProductIdentifierV2Props {
  handlePageNextInScroll: () => void;
  handleSelection: (value: boolean | string) => void;
  selectedIds: string[];
  isSelectAll: boolean;
  loadNextData: boolean;
  canEditIdentifiers?: boolean;
}

/** Unwraps a `{ name }` reference for the product popover, matching the v1 helper. */
function displayValue(value: unknown): string {
  if (isObject(value)) {
    return (value as { name?: string }).name || '';
  }
  return (value as string) || EMPTY_CELL;
}


interface RowGroupProps {
  product: RangePlanProduct;
  productIndex: number;
  columns: RangePlanTableColumn[];
  lengthEnabled: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  onToggleExpand: (index: number) => void;
  onToggleSelect: (productId: string) => void;
}

/**
 * One product: its summary row, and its combination rows once expanded.
 *
 * Memoised on the props above, so expanding one product or ticking one checkbox re-renders that
 * product alone rather than the whole page.
 */
const ProductRowGroup = memo(function ProductRowGroup({
  product,
  productIndex,
  columns,
  lengthEnabled,
  isExpanded,
  isSelected,
  onToggleExpand,
  onToggleSelect,
}: RowGroupProps): React.ReactElement {
  const { t } = useTranslation(I18N_NAMESPACE);
  const productLandingPath = useProductLandingPath();
  const view = product.productIdentifier;
  const rows: RangePlanIdentifierRow[] = view?.rows ?? [];
  const labels: ViewLabels = view?.labels ?? ({} as ViewLabels);

  /** This product's own columns, so a column it lacks renders a placeholder in the right place. */
  const ownColumns: Map<string, ResolvedColumn> = useMemo(
    () => productColumnMap(view?.columns ?? [], lengthEnabled),
    [view?.columns, lengthEnabled],
  );

  const serverColumns = columns.filter((column) => !column.isStructural);
  const summaryRow = rows[0];
  const hasMoreRows = rows.length > 1;

  /** Cells for one combination row, drawn in the page's header order. */
  const renderCells = (row: RangePlanIdentifierRow | undefined): React.ReactElement[] =>
    serverColumns.map((column) => (
      <IdentifierCell
        key={column.key}
        row={row}
        columnKey={column.key}
        definition={ownColumns.get(column.key)}
        labels={labels}
        retailCards={view?.retailCards}
        isFixed={column.isFixed}
      />
    ));

  return (
    <tbody>
      <tr>
        <td>
          <div className="common-checkbox margin-auto">
            <input
              onChange={() => onToggleSelect(product._id)}
              name={`checks_${productIndex}`}
              id={`checks_${productIndex}`}
              type="checkbox"
              checked={isSelected}
            />
            <label htmlFor={`checks_${productIndex}`} className="chklabel" />
          </div>
        </td>
        <td>
          {hasMoreRows && (
            <button
              type="button"
              className="btn-up-down"
              aria-label={
                isExpanded
                  ? t('RANGE_PLAN_PRODUCT_IDENTIFIER.COLLAPSE_ROW')
                  : t('RANGE_PLAN_PRODUCT_IDENTIFIER.EXPAND_ROW')
              }
              onClick={() => onToggleExpand(productIndex)}
            >
              <span className={isExpanded ? 'icon-new-down-arrow-icon icon-inverse' : 'icon-new-down-arrow-icon'} />
            </button>
          )}
        </td>
        <td>
          <CommonImageComponent productImage={product.image || noImage} />
        </td>
        <td>
          <div className="d-flex align-items-center">
            {shortenandHoverString(product.number || EMPTY_CELL, 30)}
            <button type="button" className="btn ml-auto" aria-label="info">
              <OverlayTrigger
                placement="bottom"
                trigger="hover"
                rootClose
                overlay={
                  <Popover id={`rp-pi-v2-info-${product._id}`} className="custom-popover">
                    <Popover.Content>
                      <p className="mb-8">
                        <b className="text-bold">Product Type: </b>
                        {displayValue(product.supplierType)}
                      </p>
                      <p className="mb-8">
                        <b className="text-bold">Description: </b>
                        {displayValue(product.description)}
                      </p>
                      <p className="mb-8">
                        <b className="text-bold">Brand: </b>
                        {displayValue(product.brandId)}
                      </p>
                      <p className="mb-8">
                        <b className="text-bold">Season: </b>
                        {displayValue(product.seasonId)}
                      </p>
                      <p className="mb-8">
                        <b className="text-bold">Colourway: </b>
                        {(product.colourLibraries ?? []).map((entry) => entry?.name).join(', ') || EMPTY_CELL}
                      </p>
                    </Popover.Content>
                  </Popover>
                }
              >
                <span className="icon icon-Information f-20" />
              </OverlayTrigger>
            </button>
            {/*
              The redirect and the combination count used to live in the Grading Group cell, which
              no longer exists: grading group is not an axis of a v2 combination. They belong to the
              product rather than to any column, so they sit with the product number.
            */}
            <Link
              target="_blank"
              to={productLandingPath(product._id)}
              className="px-2 text-decoration-none"
              title={t('RANGE_PLAN_PRODUCT_IDENTIFIER.OPEN_PRODUCT_IDENTIFIER')}
            >
              <span className="icon-redirect f-20" />
            </Link>
            {view?.rowCount ? (
              <Badge className="range-badge badge badge-rp-counter">{view.rowCount}</Badge>
            ) : null}
          </div>
        </td>
        {renderCells(summaryRow)}
      </tr>

      {isExpanded &&
        rows.slice(1).map((row) => (
          <tr className="expand-row" key={row.rowKey}>
            {/* Blank leading cells under the four structural columns; Size Range is a real column now. */}
            <td />
            <td />
            <td />
            <td />
            {renderCells(row)}
          </tr>
        ))}

      {isExpanded && view && !view.rowsComplete && (
        <tr className="expand-row">
          <td colSpan={columns.length} className="text-center">
            {t('RANGE_PLAN_PRODUCT_IDENTIFIER.ROWS_TRUNCATED', {
              shown: rows.length,
              total: view.rowCount,
            })}
          </td>
        </tr>
      )}
    </tbody>
  );
});

export default function ProductIdentifierV2(props: ProductIdentifierV2Props): React.ReactElement {
  const { handlePageNextInScroll, loadNextData, handleSelection, selectedIds, isSelectAll, canEditIdentifiers = false } = props;
  const { t } = useTranslation(I18N_NAMESPACE);
  /**
   * The two structural headers are shared with the v1 table, so they read from `common` rather
   * than this feature's namespace. Hardcoding them here bypassed i18n entirely and shouted at a
   * tenant in every locale.
   */
  const { t: tCommon } = useTranslation('common');
  const {
    rangePlanProvider: { productsNumber, productsList },
    lengthEnabled,
  } = useProductListingState() as {
    rangePlanProvider: { productsNumber: number; productsList: RangePlanProduct[] };
    lengthEnabled: boolean;
  };

  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [confirmExpandAll, setConfirmExpandAll] = useState(false);
  const barcodeActions = useIdentifierBarcodeActions(selectedIds ?? []);

  const products = useMemo(() => productsList ?? [], [productsList]);

  const columns = useRangePlanIdentifierColumns({
    columnsByProduct: useMemo(
      () => products.map((product) => product.productIdentifier?.columns ?? []),
      [products],
    ),
    lengthEnabled,
    imageHeader: tCommon('RANGE_PLANS.PRODUCT_LIST.IMAGE'),
    productNumberHeader: tCommon('RANGE_PLANS.PRODUCT_LIST.PRODUCT_NUMBER'),
  });

  /** Total combinations on the page, used to decide whether expand-all needs a warning. */
  const totalRows = useMemo(
    () => products.reduce((sum, product) => sum + (product.productIdentifier?.rowCount ?? 0), 0),
    [products],
  );

  const toggleExpand = useCallback((index: number) => {
    setExpandedRows((current) =>
      current.includes(index) ? current.filter((entry) => entry !== index) : [...current, index],
    );
  }, []);

  const toggleSelect = useCallback(
    (productId: string) => handleSelection(productId),
    [handleSelection],
  );

  const expandAll = useCallback(() => {
    setExpandedRows(products.map((_unused, index) => index));
    setConfirmExpandAll(false);
  }, [products]);

  /**
   * Expanding every product can mount tens of thousands of rows in one commit, so above the
   * threshold the user is asked first rather than handed a frozen tab.
   */
  const onExpandAllClick = useCallback(() => {
    if (expandedRows.length > 0) {
      setExpandedRows([]);
      return;
    }
    if (totalRows > EXPAND_ALL_CONFIRM_THRESHOLD) {
      setConfirmExpandAll(true);
      return;
    }
    expandAll();
  }, [expandAll, expandedRows.length, totalRows]);

  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);
  const isAllExpanded = expandedRows.length > 0;

  const hasSelection = (selectedIds ?? []).length > 0;
  const renderHeader = (column: RangePlanTableColumn): React.ReactNode => {
    const action = barcodeActionFor(column);
    if (!action || !canEditIdentifiers) return <IdentifierHeaderLabel header={column.header} />;
    const tooltipKey = action.kind === 'retail' ? 'RANGE_PLAN_PRODUCT_IDENTIFIER.ASSIGN_TOOLTIP' : 'RANGE_PLAN_PRODUCT_IDENTIFIER.GENERATE_TOOLTIP';
    return (
      <IdentifierActionHeader
        header={column.header}
        tooltipText={hasSelection ? t(tooltipKey, { label: action.label }) : t('RANGE_PLAN_PRODUCT_IDENTIFIER.SELECT_PRODUCTS_FIRST')}
        disabled={!hasSelection || barcodeActions.running}
        onAction={() => barcodeActions.request(action)}
      />
    );
  };
  const pendingAction = barcodeActions.pending;
  const confirmPrefix = pendingAction?.kind === 'gtin14' ? 'GENERATE' : 'ASSIGN';

  return (
    <div id="ProductIdentifierV2RPTableContainer" className="rp-scroll-container">
      <InfiniteScroll
        dataLength={products.length}
        hasMore={products.length < productsNumber}
        next={handlePageNextInScroll}
        loader={loadNextData ? <Loader /> : null}
        className="fixed-col-container rp-table-container"
        scrollableTarget="ProductIdentifierV2RPTableContainer"
      >
        <table id="RPProductIdentifierV2Table" className="table common-custom-table fixed-header mb-0">
          <thead>
            <tr>
              <th className="w-50px">
                <div className="common-checkbox margin-auto">
                  <input
                    type="checkbox"
                    onChange={(event) => handleSelection(event.target.checked)}
                    name="check_all_prod_identifier_v2"
                    id="check_all_prod_identifier_v2"
                    checked={isSelectAll}
                    disabled={!products.length}
                  />
                  <label htmlFor="check_all_prod_identifier_v2" className="chklabel" />
                </div>
              </th>
              <th className="w-50px">
                <button
                  type="button"
                  className="btn-up-down"
                  aria-label={isAllExpanded ? t('RANGE_PLAN_PRODUCT_IDENTIFIER.COLLAPSE_ALL') : t('RANGE_PLAN_PRODUCT_IDENTIFIER.EXPAND_ALL')}
                  onClick={onExpandAllClick}
                >
                  <span className={isAllExpanded ? 'icon-new-down-arrow-icon icon-inverse' : 'icon-new-down-arrow-icon'} />
                </button>
              </th>
              {columns.slice(2).map((column) => (
                <th
                  key={column.key}
                  className={[
                    column.key === '__rp_pi_image' ? 'w-80px' : 'w-200px',
                    column.isFixed ? 'first-fix-col' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {renderHeader(column)}
                </th>
              ))}
            </tr>
          </thead>

          {products.length > 0 ? (
            products.map((product, productIndex) => (
              <ProductRowGroup
                key={product._id}
                product={product}
                productIndex={productIndex}
                columns={columns}
                lengthEnabled={lengthEnabled}
                isExpanded={expandedRows.includes(productIndex)}
                isSelected={selectedSet.has(product._id)}
                onToggleExpand={toggleExpand}
                onToggleSelect={toggleSelect}
              />
            ))
          ) : (
            <tbody>
              <tr>
                <td colSpan={columns.length} className="text-center">
                  {t('RANGE_PLAN_PRODUCT_IDENTIFIER.NO_RECORDS_FOUND')}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </InfiniteScroll>

      <ConfirmationPopup
        onOpen={confirmExpandAll}
        toggle={() => setConfirmExpandAll(false)}
        onConfirm={expandAll}
        title={t('RANGE_PLAN_PRODUCT_IDENTIFIER.EXPAND_ALL_HEAVY_TITLE')}
        message={t('RANGE_PLAN_PRODUCT_IDENTIFIER.EXPAND_ALL_HEAVY_MESSAGE', { total: totalRows })}
      />

      <ConfirmationPopup
        onOpen={Boolean(pendingAction)}
        toggle={barcodeActions.cancel}
        onConfirm={barcodeActions.confirm}
        title={t(`RANGE_PLAN_PRODUCT_IDENTIFIER.${confirmPrefix}_CONFIRM_TITLE`, { label: pendingAction?.label ?? '' })}
        message={t(`RANGE_PLAN_PRODUCT_IDENTIFIER.${confirmPrefix}_CONFIRM_MESSAGE`, {
          label: pendingAction?.label ?? '',
          count: (selectedIds ?? []).length,
        })}
      />
    </div>
  );
}
